/**
 * The Replays screen: the player's recording library (personal bests kept
 * automatically, saved and imported recordings) with watch, compare, rename,
 * favourite, export, import and delete. Names and titles come from files and
 * storage and are untrusted: they only ever go through textContent.
 */
import { button, h } from '../editor/dom';
import { formatSeconds, type ReplayMeta } from '../replay/ReplayLibrary';
import './replays.css';

export type Click = () => void;

export interface ReplayScreenActions {
  back: Click;
  watch(m: ReplayMeta): void;
  /** Compare the two checked recordings (enabled with exactly two). */
  compare(a: ReplayMeta, b: ReplayMeta): void;
  rename(m: ReplayMeta, name: string): void;
  favourite(m: ReplayMeta, on: boolean): void;
  exportFile(m: ReplayMeta): void;
  remove(m: ReplayMeta): void;
  importFile(file: File): void;
  /** Whether the recording's track is installed (otherwise it cannot be watched here). */
  playable(m: ReplayMeta): boolean;
}

const KIND_TEXT: Readonly<Record<ReplayMeta['kind'], string>> = { pb: 'Personal best', saved: 'Saved', imported: 'Imported · view only' };

export function replaysScreen(entries: readonly ReplayMeta[], a: ReplayScreenActions, notice: string, error: string): HTMLElement {
  const checked = new Set<string>();
  const compare = button('Compare selected', () => {
    const [x, y] = entries.filter((m) => checked.has(m.id));
    if (x && y) a.compare(x, y);
  }, { className: 'secondary', disabled: true, dataset: { action: 'compare' } });
  const file = h('input', { type: 'file', accept: '.json,.xrun.json,application/json', className: 'visually-hidden', ariaLabel: 'Import a recording file' });
  file.addEventListener('change', () => {
    const f = file.files?.[0];
    if (f) a.importFile(f);
    file.value = '';
  });
  const importBtn = button('Import recording…', () => file.click(), { className: 'secondary', dataset: { action: 'import-replay' } });
  const list = h('ul', { className: 'replay-list', ariaLabel: 'Recordings' });
  for (const m of entries) {
    const box = h('input', { type: 'checkbox', ariaLabel: `Select ${m.name} for comparison` });
    box.addEventListener('change', () => {
      if (box.checked) checked.add(m.id);
      else checked.delete(m.id);
      compare.disabled = checked.size !== 2;
    });
    const name = h('input', { type: 'text', value: m.name, maxLength: 80, className: 'replay-name', ariaLabel: 'Recording name' });
    name.addEventListener('change', () => a.rename(m, name.value));
    const fav = button(m.favourite ? '★' : '☆', () => a.favourite(m, !m.favourite), { className: 'icon', title: m.favourite ? 'Unmark favourite' : 'Mark as favourite', ariaPressed: String(m.favourite), dataset: { action: 'favourite' } });
    const playable = a.playable(m);
    list.append(
      h(
        'li',
        { className: `replay-row kind-${m.kind}`, dataset: { replay: m.id, kind: m.kind } },
        box,
        fav,
        h('div', { className: 'replay-main' }, name, h('p', { className: 'replay-sub', textContent: `${m.trackTitle} · ${KIND_TEXT[m.kind]} · ${new Date(m.savedAt).toLocaleString()}` })),
        h('span', { className: 'replay-time', textContent: m.outcome === 'finished' ? formatSeconds(m.seconds) : 'unfinished' }),
        button('Watch', () => a.watch(m), { className: 'primary', disabled: !playable, title: playable ? '' : 'The track for this recording is not installed.', dataset: { action: 'watch' } }),
        button('Export', () => a.exportFile(m), { className: 'secondary', dataset: { action: 'export' } }),
        button('Delete', () => a.remove(m), { className: 'danger', dataset: { action: 'delete' } }),
      ),
    );
  }
  return h(
    'section',
    { className: 'screen screen-replays', dataset: { screen: 'replays' } },
    h('div', { className: 'screen-bar' }, button('← Menu', a.back, { className: 'back', dataset: { nav: 'back' } }), h('header', { className: 'screen-head' }, h('h1', { textContent: 'Replays' }), h('p', { className: 'lede', textContent: 'Your personal-best recordings are kept automatically. Recordings stay in this browser; export them to keep a copy.' }))),
    error ? h('p', { className: 'notice error', role: 'alert', textContent: error }) : null,
    notice ? h('p', { className: 'notice', role: 'status', textContent: notice }) : null,
    h('div', { className: 'panel-actions' }, compare, importBtn, file),
    entries.length ? list : h('p', { className: 'empty', textContent: 'No recordings yet. Finish a full run to keep your first personal best.' }),
    h('p', { className: 'panel-help', textContent: 'Imported recordings are for viewing only: they never count as your records. A recording is checked by replaying it through this build; that proves it is consistent, not that a person played it.' }),
  );
}
